import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FilterChip, TableColumn } from '@shared/components/data-table/data-table.component';
import { DateRangePickerComponent } from '@shared/components/date-range-picker/date-range-picker.component';
import {
  ToolbarPrimaryActionOption,
  ToolbarSortSelection,
} from '@shared/components/table-toolbar/table-toolbar.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { DataTableMultiSelectFilterComponent, DataTableMultiSelectFilterOption } from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';
import { MemberReferenceComponent } from '@shared/components/member-reference/member-reference.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { Person } from '../../models/person.model';
import {
  PersonEventCategory,
  PersonEvent,
} from '../../models/person-event.model';
import { PersonEventService } from '../../services/person-event.service';
import { PersonService } from '../../services/person.service';
import { NotificationService } from '@shared/services/notification.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import {
  DeleteConfirmDialogComponent,
  DeleteConfirmDialogData,
} from '@features/user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { PersonEventsContextService } from '../person-events-shell-page/person-events-context.service';
import { PersonEventEffectsComponent, buildPersonEventMovements } from '../../components/person-event-effects.component';
type PersonEventEditorKind =
  | 'ministry'
  | 'ministry-change'
  | 'ministerial-activity'
  | 'appointment'
  | 'assignment'
  | 'activity'
  | 'religious-event';
type PersonEventsViewMode = 'table' | 'timeline';

const unspecifiedLocationFilterValue = '__unspecified_location__';
const unspecifiedLocationFilterLabel = 'Sin determinar';

@Component({
  selector: 'app-person-events-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DateRangePickerComponent,
    TableSurfaceComponent,
    DataTableMultiSelectFilterComponent,
    MemberReferenceComponent,
    PersonEventEffectsComponent,
  ],
  templateUrl: './person-events-page.component.html',
  styleUrl: './person-events-page.component.scss',
})
export class PersonEventsPageComponent implements OnInit, OnDestroy {
  @ViewChild(TableSurfaceComponent) tableSurfaceRef?: TableSurfaceComponent<PersonEvent>;

  person: Person | null = null;
  events: PersonEvent[] = [];
  filteredEvents: PersonEvent[] = [];
  isLoading = true;
  errorMessage: string | null = null;
  activeFilterChips: FilterChip[] = [];
  searchTerm = '';
  sortState: ToolbarSortSelection = { sortBy: 'eventDate', direction: 'desc' };
  typeFilterOptions: { value: PersonEventCategory; label: string }[] = [];
  eventFilterOptions: string[] = [];
  locationFilterOptions: string[] = [];
  eventSelectOptions: DataTableMultiSelectFilterOption[] = [];
  locationSelectOptions: DataTableMultiSelectFilterOption[] = [];
  private typeFilterInitialized = false;
  private eventFilterInitialized = false;
  private locationFilterInitialized = false;
  canManageMembers = false;
  canManageMinisterialActivity = false;
  viewMode: PersonEventsViewMode = 'table';
  expandedEvent: PersonEvent | null = null;
  readonly eventMovements = buildPersonEventMovements;

  toggleEventMovements(event: PersonEvent): void {
    this.expandedEvent = this.expandedEvent === event ? null : event;
  }

  readonly filterForm = new FormGroup({
    dateRange: new FormGroup({
      start: new FormControl<any | null>(null),
      end: new FormControl<any | null>(null),
    }),
    type: new FormControl<PersonEventCategory[]>([], { nonNullable: true }),
    event: new FormControl<string[]>([], { nonNullable: true }),
    location: new FormControl<string[]>([], { nonNullable: true }),
  });

  readonly allColumns: TableColumn[] = [
    { key: 'eventDate', header: 'Fecha', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'category', header: 'Tipo', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'eventType', header: 'Evento', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'unit', header: 'Unidad', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'location', header: 'Lugar del acto', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'notes', header: 'Notas', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'actions', header: '', sortable: false, width: '1%', minWidth: '104px', exportable: false },
  ];
  private readonly allPrimaryActionOptions: ToolbarPrimaryActionOption[] = [
    { label: 'Actividad Ministerial', icon: 'volunteer_activism', value: 'ministerial-activity' },
    { label: 'Colaboración', icon: 'groups', value: 'activity' },
    { label: 'Sacramento o Bendición', icon: 'auto_awesome', value: 'religious-event' },
  ];

  get primaryActionOptions(): ToolbarPrimaryActionOption[] {
    return this.allPrimaryActionOptions.filter(option =>
      option.value === 'ministerial-activity'
        ? this.canManageMinisterialActivity || this.canManageMembers
        : this.canManageMembers);
  }

  private personId = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly dialog: MatDialog,
    private readonly notifications: NotificationService,
    private readonly personService: PersonService,
    private readonly personEventService: PersonEventService,
    private readonly eventsContext: PersonEventsContextService,
    private readonly userFullInfoService: UserFullInfoService,
  ) {}

  ngOnInit(): void {
    this.personId = this.eventsContext.personId || (this.route.snapshot.paramMap.get('person-id') ?? '');
    this.person = this.eventsContext.person;
    this.eventsContext.person$
      .pipe(takeUntil(this.destroy$))
      .subscribe(person => {
        this.person = person;
        this.cdr.markForCheck();
      });
    this.userFullInfoService.getUserInfo$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.canManageMembers = this.userFullInfoService.canManageMemberDataSnapshot(user);
        this.canManageMinisterialActivity = this.userFullInfoService.hasPermission('ManageMinisterialActivity', user);
        this.cdr.markForCheck();
      });
    this.filterForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyTableState();
        this.cdr.markForCheck();
      });
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentSort(): ToolbarSortSelection {
    return this.sortState;
  }

  get personProfilePath(): string[] {
    return this.getPersonBasePath();
  }

  get columns(): TableColumn[] {
    return this.canManageMembers || this.canManageMinisterialActivity
      ? this.allColumns
      : this.allColumns.filter(column => column.key !== 'actions');
  }

  canManageEvent(event: PersonEvent): boolean {
    if (event.eventKind === 'officiant') {
      return this.canManageMinisterialActivity || this.canManageMembers;
    }

    return event.category === 'MinisterialActivity'
      ? this.canManageMinisterialActivity
      : this.canManageMembers;
  }

  onToolbarSearch(term: string): void {
    this.searchTerm = term.trim();
    this.applyTableState();
    this.cdr.markForCheck();
  }

  onToolbarSort(event: ToolbarSortSelection): void {
    this.sortState = event;
    this.applyTableState();
    this.cdr.markForCheck();
  }

  setViewMode(mode: string): void {
    if (mode === 'table' || mode === 'timeline') {
      this.viewMode = mode;
    }
  }

  onPrimaryActionOption(option: ToolbarPrimaryActionOption): void {
    if (option.value === 'ministerial-activity' && !this.canManageMinisterialActivity && !this.canManageMembers) {
      return;
    }

    if (option.value !== 'ministerial-activity' && !this.canManageMembers) {
      return;
    }

    this.openCreate(option.value as PersonEventEditorKind);
  }

  navigateToUnit(unitId: string | undefined): void {
    if (unitId) {
      void this.router.navigate(['/units', unitId]);
    }
  }

  onUnitPointerDown(event: PointerEvent, unitId: string | undefined): void {
    if (event.button !== 0 || !unitId) {
      return;
    }

    event.stopPropagation();
    window.location.assign(`/units/${unitId}`);
  }

  onFilterRemove(key: string): void {
    if (key === 'search') {
      this.searchTerm = '';
      this.tableSurfaceRef?.clearSearch();
      this.applyTableState();
      this.cdr.markForCheck();
      return;
    }

    if (key === 'dateRange') {
      this.clearDateRangeFilter();
      this.applyTableState();
      this.cdr.markForCheck();
      return;
    }

    if (key === 'type') {
      this.filterForm.controls.type.setValue(this.typeFilterOptions.map(option => option.value));
    } else if (key === 'event') {
      this.filterForm.controls.event.setValue(this.eventFilterOptions);
    } else if (key === 'location') {
      this.filterForm.controls.location.setValue(this.locationFilterOptions);
    }
  }

  openCreate(kind: PersonEventEditorKind): void {
    const canCreate = this.isMinisterialActivityKind(kind)
      ? this.canManageMinisterialActivity || this.canManageMembers
      : this.canManageMembers;

    if (!canCreate) {
      return;
    }

    if (this.isMinisterialActivityKind(kind) && !this.person?.dateOfBirth) {
      this.notifications.error('Para registrar actividad ministerial, primero carga la fecha de nacimiento del miembro.');
      return;
    }

    this.router.navigate([...this.getPersonBasePath(), 'events', 'new', kind]);
  }

  onEditEvent(event: PersonEvent): void {
    if (!this.canManageEvent(event)) {
      return;
    }

    if (!event.canEdit) {
      this.notifications.error('Este registro no se puede editar desde esta pantalla.');
      return;
    }

    const kind = this.getEditorKindForEvent(event);
    if (!kind) {
      this.notifications.error('Ese registro no se puede editar desde esta pantalla.');
      return;
    }

    this.router.navigate([...this.getPersonBasePath(), 'events', kind, event.id, 'edit']);
  }

  private getPersonBasePath(): string[] {
    const allParams = this.route.snapshot.pathFromRoot.reduce(
      (acc, r) => ({ ...acc, ...r.params }), {} as Record<string, string>
    );
    return allParams['unit-id']
      ? ['/units', allParams['unit-id'], 'members', this.personId]
      : ['/people', this.personId];
  }

  private isMinisterialActivityKind(kind: PersonEventEditorKind): boolean {
    return kind === 'ministerial-activity';
  }

  onDelete(event: PersonEvent): void {
    if (!this.canManageEvent(event)) {
      return;
    }

    if (!this.person) {
      return;
    }

    if (!event.canDelete) {
      this.notifications.error('Este registro no se puede eliminar desde esta pantalla.');
      return;
    }

    const data: DeleteConfirmDialogData = {
      title: 'Eliminar evento',
      message: `Estas seguro de que queres eliminar el evento "${event.eventTypeName}"?`,
      confirmButtonText: 'Eliminar',
    };

    this.dialog
      .open(DeleteConfirmDialogComponent, modalDialogConfig({ data, size: 'sm', layout: 'confirm', tone: 'danger' }))
      .afterClosed()
      .pipe(filter(result => !!result), takeUntil(this.destroy$))
      .subscribe(() => {
      const operation$ = event.eventKind === 'officiant'
        ? this.personService.deleteOfficiantCollaboration(this.person!.id, event.id)
        : event.eventKind === 'appointment'
        ? this.personService.deleteAppointment(this.person!.id, event.id)
        : event.eventKind === 'assignment'
          ? this.personService.deleteAssignment(this.person!.id, event.id)
          : this.personEventService.deleteEvent(this.person!.id, event.category, event.id);

        operation$
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notifications.success('Evento eliminado.');
              this.reloadData();
            },
            error: err => {
              this.notifications.error(this.getErrorMessage(err, 'No se pudo eliminar el evento.'));
              this.cdr.markForCheck();
            },
          });
      });
  }

  trackByEvent(_: number, event: PersonEvent): string {
    return `${event.category}-${event.id}`;
  }

  getCategoryTheme(category: PersonEventCategory): string {
    switch (category) {
      case 'Collaboration':
        return 'collaboration';
      case 'MinisterialActivity':
        return 'ministerial-activity';
      case 'ReligiousEvent':
        return 'religious-event';
      default:
        return 'activity';
    }
  }

  getEventUnits(event: PersonEvent): Array<{ id?: string; name: string }> {
    if (event.eventKind === 'ministerial-activity' && event.eventTypeKey === 'LeaveOfAbsence') {
      return [];
    }

    const ministerialAreas = event.ministerialAreas ?? [];
    if (ministerialAreas.length > 0) {
      const displayAreas = this.getDisplayMinisterialAreas(event);
      return Array.from(
        new Map(
          displayAreas.map(area => [
            area.organizationUnitId ?? `historical:${area.organizationUnitName}`,
            {
              id: area.isHistoricalOrganizationUnit ? undefined : area.organizationUnitId,
              name: area.organizationUnitName,
            },
          ]),
        ).values(),
      );
    }

    return event.organizationUnitId && event.organizationUnitName
      ? [{ id: event.organizationUnitId, name: event.organizationUnitName }]
      : [];
  }

  private getDisplayMinisterialAreas(event: PersonEvent) {
    const ministerialAreas = event.ministerialAreas ?? [];
    if (event.eventKind !== 'ministerial-activity' || event.eventTypeKey !== 'Ordination') {
      return ministerialAreas;
    }

    const relatedAddedAreas = ministerialAreas.filter(area =>
      area.changeType === 'Added' &&
      (area.kind === 'Appointment' || area.kind === 'Assignment'));

    return relatedAddedAreas.length > 0
      ? relatedAddedAreas
      : ministerialAreas;
  }

  getPrimaryEventUnitId(event: PersonEvent): string | undefined {
    return this.getEventUnits(event).find(unit => !!unit.id)?.id;
  }

  getEventDisplayName(event: PersonEvent): string {
    if (event.eventKind === 'appointment' && event.detail) {
      return event.detail;
    }

    if (event.eventKind === 'assignment' && event.detail) {
      return event.detail;
    }

    return event.eventTypeName;
  }

  getEventDisplayDetail(event: PersonEvent): string | undefined {
    if (
      event.category === 'MinisterialActivity' &&
      (event.eventTypeKey === 'Dismissal' || event.eventTypeKey === 'Retirement')
    ) {
      return undefined;
    }

    if (event.eventKind === 'appointment' && event.detail) {
      return event.eventTypeName;
    }

    if (event.eventKind === 'assignment' && event.detail) {
      return event.eventTypeName;
    }

    return this.removeEventUnitFromDetail(event);
  }

  getRelatedPersonDisplay(event: PersonEvent): string | undefined {
    if (!event.relatedPersonName) {
      return undefined;
    }

    if (
      event.religiousEventType === 'Wedding'
      || event.religiousEventType === 'WeddingAnniversary'
      || event.religiousEventType === 'Engagement'
    ) {
      return event.relatedPersonName;
    }

    return undefined;
  }

  canLinkRelatedPerson(event: PersonEvent): boolean {
    return !!event.relatedPersonId && !!event.relatedPersonCanView;
  }

  private removeEventUnitFromDetail(event: PersonEvent): string | undefined {
    if (!event.detail || !event.organizationUnitName) {
      return event.detail;
    }

    const unitSuffix = ` - ${event.organizationUnitName}`;
    if (event.detail.endsWith(unitSuffix)) {
      return event.detail.slice(0, -unitSuffix.length).trim();
    }

    return event.detail;
  }

  getEventLocation(event: PersonEvent): string {
    if (event.location) {
      return event.location;
    }

    if (event.actOrganizationUnitName) {
      return event.actOrganizationUnitName;
    }

    if (event.category === 'ReligiousEvent') {
      return event.locationOrganizationUnitName || '';
    }

    return '';
  }

  private getEventLocationFilterValue(event: PersonEvent): string {
    return this.getEventLocation(event) || unspecifiedLocationFilterValue;
  }

  getEventLocationUnitId(event: PersonEvent): string | null {
    if (event.actOrganizationUnitId) {
      return event.actOrganizationUnitId;
    }

    if (event.category === 'ReligiousEvent' && event.locationOrganizationUnitId) {
      return event.locationOrganizationUnitId;
    }

    return null;
  }

  private loadData(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.personEventService.getEvents(this.personId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: events => {
          this.events = this.sortEvents(events);
          this.updateFilterOptions();
          this.applyTableState();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: err => {
          this.isLoading = false;
          this.errorMessage = this.getErrorMessage(err, 'No se pudo cargar la pantalla de eventos.');
          this.cdr.markForCheck();
        },
      });
  }

  private reloadData(): void {
    if (!this.person) {
      return;
    }

    forkJoin({
      person: this.eventsContext.refreshPerson(),
      events: this.personEventService.getEvents(this.person.id),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ person, events }) => {
          this.person = person;
          this.events = this.sortEvents(events);
          this.updateFilterOptions();
          this.applyTableState();
          this.cdr.markForCheck();
        },
        error: err => {
          this.notifications.error(this.getErrorMessage(err, 'No se pudieron recargar los eventos.'));
          this.cdr.markForCheck();
        },
      });
  }

  private applyTableState(): void {
    const filters = this.filterForm.getRawValue();
    const search = this.normalizeText(this.searchTerm);
    const start = filters.dateRange?.start ? this.toIsoDate(filters.dateRange.start) : null;
    const end = filters.dateRange?.end ? this.toIsoDate(filters.dateRange.end) : null;

    let result = this.events.filter(event => {
      const eventDate = event.eventDate ?? '';

      if (start && eventDate < start) {
        return false;
      }

      if (end && eventDate > end) {
        return false;
      }

      if (this.typeFilterOptions.length > 0 &&
          (filters.type.length === 0 || !filters.type.includes(event.category))) {
        return false;
      }

      if (this.eventFilterOptions.length > 0 &&
          (filters.event.length === 0 || !filters.event.includes(this.getEventDisplayName(event)))) {
        return false;
      }

      if (this.locationFilterOptions.length > 0 &&
          (filters.location.length === 0 || !filters.location.includes(this.getEventLocationFilterValue(event)))) {
        return false;
      }

      if (!search) {
        return true;
      }

      return this.getSearchText(event).includes(search);
    });

    result = this.sortFilteredEvents(result);
    this.filteredEvents = result;
    this.buildFilterChips();
  }

  private updateFilterOptions(): void {
    const typeOptions = this.events.map(event => ({
      value: event.category,
      label: event.categoryName,
    }));

    this.typeFilterOptions = [...new Map(typeOptions.map(option => [option.value, option])).values()]
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

    this.eventFilterOptions = this.uniqueSorted(
      this.events.map(event => this.getEventDisplayName(event)).filter(Boolean)
    );

    this.locationFilterOptions = this.uniqueSorted(
      this.events.map(event => this.getEventLocationFilterValue(event))
    );
    this.eventSelectOptions = this.eventFilterOptions.map(value => ({ value }));
    this.locationSelectOptions = this.locationFilterOptions.map(value => ({
      value,
      label: value === unspecifiedLocationFilterValue ? unspecifiedLocationFilterLabel : value,
    }));

    this.initializeMultiSelectFilters();
  }

  private buildFilterChips(): void {
    const filters = this.filterForm.getRawValue();
    const chips: FilterChip[] = [];

    if (this.searchTerm) {
      chips.push({ key: 'search', label: `Busqueda: ${this.searchTerm}`, removable: true });
    }

    const start = filters.dateRange?.start ? this.toIsoDate(filters.dateRange.start) : null;
    const end = filters.dateRange?.end ? this.toIsoDate(filters.dateRange.end) : null;
    if (start || end) {
      chips.push({ key: 'dateRange', label: `Fecha: ${start || '...'} - ${end || '...'}`, removable: true });
    }

    this.addMultiSelectChip(chips, 'type', 'Tipo', filters.type, this.typeFilterOptions);
    this.addMultiSelectChip(chips, 'event', 'Evento', filters.event, this.eventSelectOptions);
    this.addMultiSelectChip(chips, 'location', 'Lugar', filters.location, this.locationSelectOptions);

    this.activeFilterChips = chips;
  }

  private clearDateRangeFilter(): void {
    this.filterForm.controls.dateRange.reset({ start: null, end: null }, { emitEvent: false });
    this.filterForm.controls.dateRange.updateValueAndValidity({ emitEvent: false });
  }

  private initializeMultiSelectFilters(): void {
    if (!this.typeFilterInitialized && this.typeFilterOptions.length > 0) {
      this.typeFilterInitialized = true;
      this.filterForm.controls.type.setValue(this.typeFilterOptions.map(option => option.value), { emitEvent: false });
    }
    if (!this.eventFilterInitialized && this.eventFilterOptions.length > 0) {
      this.eventFilterInitialized = true;
      this.filterForm.controls.event.setValue(this.eventFilterOptions, { emitEvent: false });
    }
    if (!this.locationFilterInitialized && this.locationFilterOptions.length > 0) {
      this.locationFilterInitialized = true;
      this.filterForm.controls.location.setValue(this.locationFilterOptions, { emitEvent: false });
    }
  }

  private addMultiSelectChip(
    chips: FilterChip[],
    key: string,
    label: string,
    values: string[],
    options: DataTableMultiSelectFilterOption[],
  ): void {
    if (values.length === options.length) {
      return;
    }

    const selection = values.length === 0
      ? 'Ninguno'
      : values.map(value => options.find(option => option.value === value)?.label ?? value).join(', ');
    chips.push({ key, label: `${label}: ${selection}`, removable: true });
  }

  private sortFilteredEvents(events: PersonEvent[]): PersonEvent[] {
    return [...events].sort((a, b) => {
      const valA = this.getSortValue(a, this.sortState.sortBy);
      const valB = this.getSortValue(b, this.sortState.sortBy);
      const result = typeof valA === 'number' && typeof valB === 'number'
        ? valA - valB
        : String(valA).localeCompare(String(valB), 'es', { sensitivity: 'base' });

      return this.sortState.direction === 'asc' ? result : -result;
    });
  }

  private getSortValue(event: PersonEvent, sortBy: string): string | number {
    switch (sortBy) {
      case 'eventDate':
        return event.eventDate ?? '';
      case 'category':
        return event.categoryName ?? '';
      case 'eventType':
        return this.getEventDisplayName(event);
      case 'unit':
        return this.getEventUnits(event).map(unit => unit.name).join(', ');
      case 'location':
        return this.getEventLocation(event);
      case 'notes':
        return event.notes ?? '';
      default:
        return '';
    }
  }

  private getSearchText(event: PersonEvent): string {
    return this.normalizeText([
      event.eventDate,
      this.formatDate(event.eventDate),
      event.endDate,
      this.formatDate(event.endDate),
      event.categoryName,
      this.getEventDisplayName(event),
      this.getEventDisplayDetail(event),
      this.getRelatedPersonDisplay(event),
      event.relatedPersonName,
      this.getEventLocation(event),
      this.getEventUnits(event).map(unit => unit.name).join(' '),
      event.actOrganizationUnitName,
      event.notes,
    ].filter(Boolean).join(' '));
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  }

  private toIsoDate(value: any): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private normalizeText(value: string): string {
    return value
      .toLocaleLowerCase('es')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private uniqueSorted(values: string[]): string[] {
    return [...new Set(values)].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }

  private sortEvents(events: PersonEvent[]): PersonEvent[] {
    return [...events].sort((a, b) => (b.eventDate ?? '').localeCompare(a.eventDate ?? ''));
  }

  private getEditorKindForEvent(event: PersonEvent): PersonEventEditorKind | null {
    switch (event.category) {
      case 'Collaboration':
        return 'activity';
      case 'MinisterialActivity':
        return event.eventKind === 'ministerial-activity' || event.eventKind === 'officiant'
          ? 'ministerial-activity'
          : null;
      case 'ReligiousEvent':
        return 'religious-event';
      default:
        return null;
    }
  }

  private getErrorMessage(err: unknown, fallback: string): string {
    if (!err || typeof err !== 'object') {
      return fallback;
    }

    const error = err as {
      error?: { detail?: string; title?: string };
      response?: string;
      detail?: string;
      message?: string;
    };

    if (error.error?.detail) {
      return error.error.detail;
    }

    if (error.detail) {
      return error.detail;
    }

    if (error.response) {
      try {
        const parsed = JSON.parse(error.response) as { detail?: string; title?: string };
        if (parsed.detail) {
          return parsed.detail;
        }
        if (parsed.title) {
          return parsed.title;
        }
      } catch {
        // Ignore parse errors.
      }
    }

    if (error.error?.title) {
      return error.error.title;
    }

    if (error.message && error.message !== 'An unexpected server error occurred.') {
      return error.message;
    }

    return fallback;
  }
}
